import React, { useEffect, useMemo, useState } from "react";
import { api, API, fmtDate, fmtINR, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, ArrowDownCircle, ArrowUpCircle, ArrowRightCircle, Calculator, Wallet, PlusCircle, MinusCircle, Trash, Download, Printer } from "lucide-react";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar from "@/components/crm/Filters";
import ItemAutocomplete from "@/components/crm/ItemAutocomplete";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CATS = [
  { value: "project_amount", label: "Project Amount (Customer Paid — Credit)", short: "PROJECT", color: "emerald", Icon: ArrowDownCircle, sign: "+" },
  { value: "other_credit", label: "Other Credit (Refund / Adjustment / Bonus)", short: "OTHER Cr", color: "blue", Icon: PlusCircle, sign: "+" },
  { value: "used", label: "Kya Laga (Material Used — Debit)", short: "USED", color: "rose", Icon: ArrowUpCircle, sign: "−" },
  { value: "expense", label: "Expense (Labor / Transport — Debit)", short: "EXPENSE", color: "slate", Icon: ArrowRightCircle, sign: "−" },
  { value: "other_debit", label: "Other Debit (Misc outflow)", short: "OTHER Db", color: "amber", Icon: MinusCircle, sign: "−" },
];

const emptyItem = { item_name: "", qty: 1, unit: "Nos", rate: 0, kharcha: 0 };
const empty = {
  date: new Date().toISOString().slice(0,10),
  category: "used",
  project_id: "", customer_id: "", notes: "",
  items: [{...emptyItem}],
};

export default function Hisaab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [custSummary, setCustSummary] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("ALL");
  const [projectFilter, setProjectFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [saving, setSaving] = useState(false);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (projectFilter) p.set("project_id", projectFilter);
    if (customerFilter) p.set("customer_id", customerFilter);
    if (fFrom) p.set("from_date", fFrom);
    if (fTo) p.set("to_date", fTo);
    if (catFilter !== "ALL") p.set("category", catFilter);
    return p.toString();
  };
  const load = async () => {
    const qs = buildParams();
    try { setRows((await api.get(`/hisaab${qs?`?${qs}`:""}`)).data); } catch { setRows([]); }
    const sumQs = new URLSearchParams();
    if (projectFilter) sumQs.set("project_id", projectFilter);
    if (customerFilter) sumQs.set("customer_id", customerFilter);
    if (fFrom) sumQs.set("from_date", fFrom);
    if (fTo) sumQs.set("to_date", fTo);
    try { setSummary((await api.get(`/hisaab/summary${sumQs.toString()?`?${sumQs}`:""}`)).data); } catch { setSummary(null); }
    try {
      const cq = new URLSearchParams();
      if (fFrom) cq.set("from_date", fFrom);
      if (fTo) cq.set("to_date", fTo);
      setCustSummary((await api.get(`/hisaab/customer-summary${cq.toString()?`?${cq}`:""}`)).data);
    } catch { setCustSummary([]); }
    try { setProjects((await api.get("/projects")).data); } catch { setProjects([]); }
    try { setCustomers((await api.get("/customers")).data); } catch { setCustomers([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [catFilter, projectFilter, customerFilter, fFrom, fTo]);

  const custName = (id) => {
    if (!id) return "";
    const c = customers.find(x => x.id === id);
    return c ? c.name : id.slice(0,8);
  };

  const addItem = () => setForm(f => ({...f, items: [...f.items, {...emptyItem}]}));
  const rmItem = (i) => setForm(f => ({...f, items: f.items.filter((_,idx)=>idx!==i)}));
  const setItem = (i, k, v) => setForm(f => { const arr=[...f.items]; arr[i]={...arr[i], [k]:v}; return {...f, items:arr}; });

  const save = async () => {
    setSaving(true);
    try {
      const shared = {
        date: form.date, category: form.category, notes: form.notes || "",
      };
      if (form.customer_id) shared.customer_id = form.customer_id;
      if (form.project_id) shared.project_id = form.project_id;

      const valid = (form.items || []).filter(it => (it.item_name || "").trim() && (parseFloat(it.qty)||0) > 0);
      if (valid.length === 0) { toast.error("Kam se kam ek item + qty add karo"); setSaving(false); return; }

      if (editId) {
        // Edit mode: PATCH original row, then POST any additional items as new entries
        const first = valid[0];
        await api.patch(`/hisaab/${editId}`, {
          ...shared,
          item_name: first.item_name, qty: parseFloat(first.qty)||0, unit: first.unit||"Nos",
          rate: parseFloat(first.rate)||0,
        });
        let added = 0, kharchaCount = 0;
        const firstKh = parseFloat(first.kharcha)||0;
        if (firstKh > 0) {
          await api.post("/hisaab", { ...shared, category: "expense",
            item_name: `Kharcha - ${first.item_name}`, qty: 1, unit: "Job", rate: firstKh });
          kharchaCount++;
        }
        for (let i = 1; i < valid.length; i++) {
          const it = valid[i];
          await api.post("/hisaab", {
            ...shared,
            item_name: it.item_name, qty: parseFloat(it.qty)||0, unit: it.unit||"Nos",
            rate: parseFloat(it.rate)||0,
          });
          added++;
          const kh = parseFloat(it.kharcha)||0;
          if (kh > 0) {
            await api.post("/hisaab", { ...shared, category: "expense",
              item_name: `Kharcha - ${it.item_name}`, qty: 1, unit: "Job", rate: kh });
            kharchaCount++;
          }
        }
        toast.success(`Entry updated${added?` + ${added} new`:""}${kharchaCount?` + ${kharchaCount} kharcha`:""}`);
      } else {
        // Create mode: loop POST one per item + auto-expense entry for each item's kharcha
        let mainCount = 0, kharchaCount = 0;
        for (const it of valid) {
          await api.post("/hisaab", {
            ...shared,
            item_name: it.item_name, qty: parseFloat(it.qty)||0, unit: it.unit||"Nos",
            rate: parseFloat(it.rate)||0,
          });
          mainCount++;
          const kh = parseFloat(it.kharcha)||0;
          if (kh > 0) {
            await api.post("/hisaab", {
              ...shared, category: "expense",
              item_name: `Kharcha - ${it.item_name}`,
              qty: 1, unit: "Job", rate: kh,
            });
            kharchaCount++;
          }
        }
        toast.success(`${mainCount} ${mainCount===1?"entry":"entries"}${kharchaCount?` + ${kharchaCount} kharcha`:""} saved`);
      }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const edit = (r) => {
    setForm({
      ...empty,
      date: r.date, category: r.category,
      project_id: r.project_id || "", customer_id: r.customer_id || "",
      notes: r.notes || "",
      items: [{ item_name: r.item_name, qty: r.qty, unit: r.unit||"Nos", rate: r.rate, kharcha: 0 }],
    });
    setEditId(r.id); setOpen(true);
  };
  const del = async (r) => { try { await api.delete(`/hisaab/${r.id}`); toast.success("Deleted"); load(); } catch(e){ toast.error(formatError(e.response?.data?.detail)); } };

  // ============== QUICK PROJECT SETUP ==============
  const emptyQuickItem = { item_name: "", qty: 1, unit: "Nos", rate: 0 };
  const emptyQuick = {
    customer_id: "", project_id: "",
    date: new Date().toISOString().slice(0,10),
    project_amount: 0, project_desc: "Project Amount — Sale Value",
    received_amount: 0, received_mode: "IMPS", received_ref: "",
    used_items: [{...emptyQuickItem}],
    expense_items: [{...emptyQuickItem}],
    other_credit: 0, other_credit_desc: "Other Credit",
    other_debit: 0, other_debit_desc: "Other Debit",
    notes: "",
  };
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState(emptyQuick);
  const [quickSaving, setQuickSaving] = useState(false);

  const setQuickField = (k, v) => setQuick(q => ({...q, [k]: v}));
  const addUsed = () => setQuick(q => ({...q, used_items: [...q.used_items, {...emptyQuickItem}]}));
  const rmUsed = (i) => setQuick(q => ({...q, used_items: q.used_items.filter((_,idx)=>idx!==i)}));
  const setUsed = (i, k, v) => setQuick(q => { const arr=[...q.used_items]; arr[i]={...arr[i], [k]:v}; return {...q, used_items:arr}; });
  const addExp = () => setQuick(q => ({...q, expense_items: [...q.expense_items, {...emptyQuickItem}]}));
  const rmExp = (i) => setQuick(q => ({...q, expense_items: q.expense_items.filter((_,idx)=>idx!==i)}));
  const setExp = (i, k, v) => setQuick(q => { const arr=[...q.expense_items]; arr[i]={...arr[i], [k]:v}; return {...q, expense_items:arr}; });

  const saveQuick = async () => {
    if (!quick.customer_id) { toast.error("Customer select karo"); return; }
    setQuickSaving(true);
    try {
      const shared = { customer_id: quick.customer_id, date: quick.date, notes: quick.notes || "" };
      if (quick.project_id) shared.project_id = quick.project_id;
      let entryCount = 0, paymentCount = 0;

      const proj = parseFloat(quick.project_amount) || 0;
      if (proj > 0) {
        await api.post("/hisaab", { ...shared, category: "project_amount",
          item_name: quick.project_desc || "Project Amount", qty: 1, unit: "Job", rate: proj });
        entryCount++;
      }
      for (const it of quick.used_items) {
        if (!(it.item_name||"").trim() || (parseFloat(it.qty)||0) <= 0) continue;
        await api.post("/hisaab", { ...shared, category: "used",
          item_name: it.item_name, qty: parseFloat(it.qty)||1, unit: it.unit||"Nos", rate: parseFloat(it.rate)||0 });
        entryCount++;
      }
      for (const it of quick.expense_items) {
        if (!(it.item_name||"").trim() || (parseFloat(it.qty)||0) <= 0) continue;
        await api.post("/hisaab", { ...shared, category: "expense",
          item_name: it.item_name, qty: parseFloat(it.qty)||1, unit: it.unit||"Nos", rate: parseFloat(it.rate)||0 });
        entryCount++;
      }
      const oc = parseFloat(quick.other_credit) || 0;
      if (oc > 0) {
        await api.post("/hisaab", { ...shared, category: "other_credit",
          item_name: quick.other_credit_desc || "Other Credit", qty: 1, unit: "-", rate: oc });
        entryCount++;
      }
      const od = parseFloat(quick.other_debit) || 0;
      if (od > 0) {
        await api.post("/hisaab", { ...shared, category: "other_debit",
          item_name: quick.other_debit_desc || "Other Debit", qty: 1, unit: "-", rate: od });
        entryCount++;
      }
      const rec = parseFloat(quick.received_amount) || 0;
      if (rec > 0) {
        const cust = customers.find(c => c.id === quick.customer_id);
        await api.post("/payments", {
          date: quick.date, direction: "IN", amount: rec,
          mode: quick.received_mode || "IMPS",
          customer_id: quick.customer_id,
          party_name: cust?.name || "",
          reference: quick.received_ref || "",
          category: "Project Payment",
        });
        paymentCount++;
      }
      toast.success(`Saved ${entryCount} Hisaab entr${entryCount===1?"y":"ies"}${paymentCount?` + 1 Payment`:""}`);
      setQuickOpen(false); setQuick(emptyQuick); load();
    } catch(e) {
      toast.error(formatError(e.response?.data?.detail) || "Save failed");
    } finally { setQuickSaving(false); }
  };

  const qPreview = useMemo(() => {
    const proj = parseFloat(quick.project_amount) || 0;
    const usedTotal = quick.used_items.reduce((s, it) => s + (parseFloat(it.qty)||0)*(parseFloat(it.rate)||0), 0);
    const expTotal = quick.expense_items.reduce((s, it) => s + (parseFloat(it.qty)||0)*(parseFloat(it.rate)||0), 0);
    const totalCost = usedTotal + expTotal;
    const oc = parseFloat(quick.other_credit) || 0;
    const od = parseFloat(quick.other_debit) || 0;
    const rec = parseFloat(quick.received_amount) || 0;
    const totalCredit = proj + oc;
    const totalDebit = totalCost + od;
    const profit = totalCredit - totalDebit;
    const balance = totalCredit - rec;
    const entries = (proj>0?1:0)
      + quick.used_items.filter(it=>(it.item_name||"").trim()).length
      + quick.expense_items.filter(it=>(it.item_name||"").trim()).length
      + (oc>0?1:0) + (od>0?1:0);
    return { proj, usedTotal, expTotal, totalCost, oc, od, rec, totalCredit, totalDebit, profit, balance, entries };
  }, [quick]);
  // =============================================


  const download = (fmt, cid) => {
    const p = new URLSearchParams();
    const useCust = cid !== undefined ? cid : customerFilter;
    if (useCust) p.set("customer_id", useCust);
    if (cid === undefined) {
      // Only apply page filters when downloading page-wide (not per-customer row)
      if (projectFilter) p.set("project_id", projectFilter);
      if (catFilter !== "ALL") p.set("category", catFilter);
    }
    if (fFrom) p.set("from_date", fFrom);
    if (fTo) p.set("to_date", fTo);
    const qs = p.toString();
    api.get(`/hisaab/export.${fmt}${qs?`?${qs}`:""}`, { responseType: "blob" }).then(r => {
      const blob = new Blob([r.data], { type: fmt === "csv" ? "text/csv" : "application/json" });
      const label = useCust ? custName(useCust).replace(/\s+/g,"_") : "all";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `hisaab-${label}-${new Date().toISOString().slice(0,10)}.${fmt}`;
      link.click();
      toast.success(`Hisaab ${fmt.toUpperCase()} download start ho gaya${useCust?` (${custName(useCust)})`:""}`);
    }).catch(e => toast.error(formatError(e.response?.data?.detail) || "Download failed"));
  };

  const openPrint = (cid) => {
    const p = new URLSearchParams();
    const useCust = cid !== undefined ? cid : customerFilter;
    if (useCust) p.set("customer_id", useCust);
    if (cid === undefined) {
      if (projectFilter) p.set("project_id", projectFilter);
      if (catFilter !== "ALL") p.set("category", catFilter);
    }
    if (fFrom) p.set("from_date", fFrom);
    if (fTo) p.set("to_date", fTo);
    window.open(`/hisaab/print${p.toString()?`?${p}`:""}`, "_blank");
  };

  const visible = rows.filter(r => matchesQuery(r, search));
  const catMeta = (c) => CATS.find(x => x.value === c) || CATS[0];
  const catBadge = (c) => {
    const m = catMeta(c);
    return <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-${m.color}-100 text-${m.color}-800`}>{c.replace("_"," ")}</span>;
  };

  const previewTotals = useMemo(() => {
    let amount = 0, kharcha = 0;
    (form.items||[]).forEach(it => {
      amount += (parseFloat(it.qty)||0) * (parseFloat(it.rate)||0);
      kharcha += (parseFloat(it.kharcha)||0);
    });
    return { amount, kharcha, grand: amount + kharcha };
  }, [form.items]);

  return (
    <div className="space-y-5" data-testid="hisaab-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Hisaab <span className="text-slate-400 text-lg font-medium">/ हिसाब · Profit Tracker</span></h1>
          <p className="text-slate-500 text-sm">Project Amount − (Kya Laga + Expense + Other Debit) + Other Credit = <b>Net Profit</b>. Sab hisaab customer-wise.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search item / notes..." testid="hisaab-search-input"/>
          <Button variant="outline" onClick={()=>download("csv")} data-testid="hisaab-download-csv" className="border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100">
            <Download className="w-4 h-4 mr-1"/>{customerFilter ? "CSV (Customer)" : "CSV (Excel)"}
          </Button>
          <Button variant="outline" onClick={()=>openPrint()} data-testid="hisaab-download-pdf" className="border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100">
            <Printer className="w-4 h-4 mr-1"/>PDF{customerFilter?" (Customer)":""}
          </Button>
          <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if(!v){ setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-300" data-testid="add-hisaab-btn"><Plus className="w-4 h-4 mr-1"/>Single Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-amber-600"/>{editId?"Edit":"New"} Hisaab Entry <span className="text-xs font-normal text-slate-500">— {editId?"single row":"multiple items + har item ka kharcha (labor/misc) bhi add kar sakte ho"}</span></DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Category*</Label>
                  <Select value={form.category} onValueChange={v=>setForm({...form, category:v})}>
                    <SelectTrigger data-testid="hisaab-category"><SelectValue/></SelectTrigger>
                    <SelectContent>{CATS.map(c=><SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Date*</Label>
                  <Input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}/></div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Customer (optional — customer-wise hisaab track karne ke liye)</Label>
                  <Select value={form.customer_id||"__NONE__"} onValueChange={v=>setForm({...form, customer_id: v==="__NONE__"?"":v})}>
                    <SelectTrigger data-testid="hisaab-customer"><SelectValue placeholder="No customer (general entry)"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__NONE__">— No Customer —</SelectItem>
                      {customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Project (optional)</Label>
                  <Select value={form.project_id||"__NONE__"} onValueChange={v=>setForm({...form, project_id: v==="__NONE__"?"":v})}>
                    <SelectTrigger data-testid="hisaab-project"><SelectValue placeholder="No project (general entry)"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__NONE__">— No Project —</SelectItem>
                      {projects.map(p=><SelectItem key={p.id} value={p.id}>{p.project_no} — {p.customer_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-slate-600">Items ({form.items.length}) — Sabhi ek hi customer/date/category ke andar save honge{editId?" · First row updates, extra rows create new entries":""}</Label>
                    <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="hisaab-add-item"><PlusCircle className="w-3.5 h-3.5 mr-1"/>Add Item</Button>
                  </div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1 grid grid-cols-13 gap-1 px-1" style={{gridTemplateColumns:'repeat(13, minmax(0,1fr))'}}>
                    <div className="col-span-1">Sr</div>
                    <div className="col-span-3">Item Name</div>
                    <div className="col-span-1 text-right">Qty</div>
                    <div className="col-span-1">Unit</div>
                    <div className="col-span-2 text-right">Rate (₹)</div>
                    <div className="col-span-2 text-right">Amount</div>
                    <div className="col-span-2 text-right">Kharcha ₹ <span className="text-slate-400 normal-case">(labor/misc)</span></div>
                    <div className="col-span-1"></div>
                  </div>
                  <div className="space-y-1">
                  {form.items.map((it, i)=>{
                    const amt = (parseFloat(it.qty)||0)*(parseFloat(it.rate)||0);
                    return (
                    <div key={i} className="grid gap-1 items-center bg-slate-50 border border-slate-200 rounded-md p-1.5" style={{gridTemplateColumns:'repeat(13, minmax(0,1fr))'}}>
                      <div className="col-span-1 text-slate-500 font-mono text-xs pl-1">{i+1}</div>
                      <div className="col-span-3">
                        <ItemAutocomplete
                          value={it.item_name}
                          onChange={(n)=>setItem(i,"item_name",n)}
                          onPick={(picked)=>{
                            const arr=[...form.items];
                            arr[i]={...arr[i], item_name:picked.name,
                              unit: picked.unit||arr[i].unit,
                              rate: picked.last_rate||arr[i].rate};
                            setForm({...form, items:arr});
                          }}
                          placeholder="Type item name"
                          testid={`hisaab-item-name-${i}`}
                        />
                      </div>
                      <div className="col-span-1"><Input type="number" step="0.01" className="h-9 text-right" value={it.qty} onChange={e=>setItem(i,"qty",e.target.value)} data-testid={`hisaab-qty-${i}`}/></div>
                      <div className="col-span-1"><Input className="h-9" value={it.unit||""} onChange={e=>setItem(i,"unit",e.target.value)} placeholder="Nos"/></div>
                      <div className="col-span-2"><Input type="number" step="0.01" className="h-9 text-right" value={it.rate} onChange={e=>setItem(i,"rate",e.target.value)} data-testid={`hisaab-rate-${i}`}/></div>
                      <div className="col-span-2 text-right font-mono text-xs text-slate-700 pt-2">{fmtINR(amt)}</div>
                      <div className="col-span-2"><Input type="number" step="0.01" className="h-9 text-right bg-amber-50" value={it.kharcha||0} onChange={e=>setItem(i,"kharcha",e.target.value)} placeholder="0" data-testid={`hisaab-kharcha-${i}`}/></div>
                      <div className="col-span-1 text-right">
                        {form.items.length>1 && (i > 0 || !editId) && (
                          <Button type="button" size="icon" variant="ghost" onClick={()=>rmItem(i)} className="text-rose-600 h-7 w-7"><Trash className="w-3.5 h-3.5"/></Button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                  </div>
                </div>

                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Notes (sabhi items par apply hoga)</Label>
                  <Textarea rows={2} value={form.notes||""} onChange={e=>setForm({...form, notes:e.target.value})} data-testid="hisaab-notes"/></div>

                <div className="sm:col-span-2 bg-slate-50 rounded-lg border border-slate-200 p-3 grid grid-cols-3 gap-3 text-xs">
                  <div className="text-center">
                    <div className="text-slate-500 uppercase text-[10px] font-bold">Item Amount ({form.items.length} items)</div>
                    <div className="font-mono font-bold text-slate-900 text-base" data-testid="preview-amount">{fmtINR(previewTotals.amount)}</div>
                  </div>
                  <div className="text-center border-l border-r border-slate-200">
                    <div className="text-amber-700 uppercase text-[10px] font-bold">+ Kharcha (Auto-Expense)</div>
                    <div className="font-mono font-bold text-amber-800 text-base" data-testid="preview-kharcha">{fmtINR(previewTotals.kharcha)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-700 uppercase text-[10px] font-bold">Grand Total</div>
                    <div className="font-mono font-black text-emerald-800 text-base" data-testid="preview-grand">{fmtINR(previewTotals.grand)}</div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={saving || !(form.items||[]).some(it=>(it.item_name||"").trim())} data-testid="save-hisaab-btn">
                  {saving ? "Saving..." : (editId
                    ? (form.items.length>1 ? `Update + Add ${form.items.length-1} New` : "Update")
                    : `Save ${form.items.length} ${form.items.length===1?"Entry":"Entries"}`)}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ============== QUICK PROJECT SETUP DIALOG ============== */}
          <Dialog open={quickOpen} onOpenChange={(v)=>{ setQuickOpen(v); if(!v) setQuick(emptyQuick); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md" data-testid="add-quick-project-btn"><Wallet className="w-4 h-4 mr-1"/>Full Project Hisaab</Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-emerald-600"/>Full Project Hisaab — Ek Customer Ka Pura Setup
                  <span className="text-xs font-normal text-slate-500">— Project Amount + Cost + Received sab ek saath</span>
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 pb-3 border-b border-slate-200">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Customer* <span className="text-rose-600">(required)</span></Label>
                  <Select value={quick.customer_id||"__NONE__"} onValueChange={v=>setQuickField("customer_id", v==="__NONE__"?"":v)}>
                    <SelectTrigger data-testid="quick-customer"><SelectValue placeholder="Select customer"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__NONE__">— Select Customer —</SelectItem>
                      {customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Date</Label>
                  <Input type="date" value={quick.date} onChange={e=>setQuickField("date", e.target.value)} data-testid="quick-date"/>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Project (optional)</Label>
                  <Select value={quick.project_id||"__NONE__"} onValueChange={v=>setQuickField("project_id", v==="__NONE__"?"":v)}>
                    <SelectTrigger data-testid="quick-project"><SelectValue placeholder="No project"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__NONE__">— No Project —</SelectItem>
                      {projects.filter(p=>!quick.customer_id || p.customer_id===quick.customer_id).map(p=><SelectItem key={p.id} value={p.id}>{p.project_no} — {p.customer_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* PROJECT AMOUNT (Credit) */}
              <div className="mb-4 border-l-4 border-emerald-500 bg-emerald-50 p-3 rounded-r">
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-800 mb-2 flex items-center gap-1.5">
                  <ArrowDownCircle className="w-4 h-4"/>💰 Project Amount (Total Order / Sale Value — Credit)
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Input value={quick.project_desc} onChange={e=>setQuickField("project_desc", e.target.value)} placeholder="e.g. 5kW ONGRID Solar - Rakesh Sharma" data-testid="quick-project-desc"/>
                  </div>
                  <Input type="number" step="0.01" value={quick.project_amount} onChange={e=>setQuickField("project_amount", e.target.value)} placeholder="Amount ₹" className="font-mono text-right" data-testid="quick-project-amount"/>
                </div>
              </div>

              {/* RECEIVED (creates Payment IN) */}
              <div className="mb-4 border-l-4 border-emerald-700 bg-emerald-50/60 p-3 rounded-r">
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-900 mb-2 flex items-center gap-1.5">
                  <ArrowDownCircle className="w-4 h-4"/>💳 Received Amount (Payment IN — Customer se paisa aya)
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <Input type="number" step="0.01" value={quick.received_amount} onChange={e=>setQuickField("received_amount", e.target.value)} placeholder="Received ₹" className="font-mono text-right" data-testid="quick-received-amount"/>
                  <Select value={quick.received_mode} onValueChange={v=>setQuickField("received_mode", v)}>
                    <SelectTrigger data-testid="quick-received-mode"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["IMPS","UPI","NEFT","RTGS","Cash","Cheque","Card","Other"].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={quick.received_ref} onChange={e=>setQuickField("received_ref", e.target.value)} placeholder="UTR / Ref no." className="col-span-2" data-testid="quick-received-ref"/>
                </div>
              </div>

              {/* USED ITEMS (Debit) */}
              <div className="mb-4 border-l-4 border-rose-500 bg-rose-50 p-3 rounded-r">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-rose-800 flex items-center gap-1.5">
                    <ArrowUpCircle className="w-4 h-4"/>🔨 Kya Laga / Material Used (Debit)
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addUsed} className="text-rose-700 border-rose-300" data-testid="quick-add-used"><PlusCircle className="w-3.5 h-3.5 mr-1"/>Add Row</Button>
                </div>
                <div className="text-[9px] uppercase text-slate-500 font-semibold grid grid-cols-11 gap-1 mb-1 px-1">
                  <div className="col-span-4">Item Name</div>
                  <div className="col-span-1 text-right">Qty</div>
                  <div className="col-span-1">Unit</div>
                  <div className="col-span-2 text-right">Rate ₹</div>
                  <div className="col-span-2 text-right">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="space-y-1">
                {quick.used_items.map((it, i) => {
                  const amt = (parseFloat(it.qty)||0)*(parseFloat(it.rate)||0);
                  return (
                  <div key={i} className="grid grid-cols-11 gap-1 items-center">
                    <div className="col-span-4"><ItemAutocomplete value={it.item_name} onChange={n=>setUsed(i,"item_name",n)} onPick={p=>{const arr=[...quick.used_items];arr[i]={...arr[i],item_name:p.name,unit:p.unit||arr[i].unit,rate:p.last_rate||arr[i].rate};setQuickField("used_items",arr);}} placeholder="e.g. Waaree 585W Panel" testid={`quick-used-name-${i}`}/></div>
                    <div className="col-span-1"><Input type="number" step="0.01" className="h-9 text-right font-mono" value={it.qty} onChange={e=>setUsed(i,"qty",e.target.value)} data-testid={`quick-used-qty-${i}`}/></div>
                    <div className="col-span-1"><Input className="h-9" value={it.unit||""} onChange={e=>setUsed(i,"unit",e.target.value)} placeholder="Nos"/></div>
                    <div className="col-span-2"><Input type="number" step="0.01" className="h-9 text-right font-mono" value={it.rate} onChange={e=>setUsed(i,"rate",e.target.value)} data-testid={`quick-used-rate-${i}`}/></div>
                    <div className="col-span-2 text-right font-mono text-xs font-semibold text-rose-700 pt-2">{fmtINR(amt)}</div>
                    <div className="col-span-1 text-right">{quick.used_items.length>1 && <Button type="button" size="icon" variant="ghost" onClick={()=>rmUsed(i)} className="text-rose-600 h-7 w-7"><Trash className="w-3.5 h-3.5"/></Button>}</div>
                  </div>);
                })}
                </div>
                <div className="text-right text-xs font-bold text-rose-800 mt-2">Used Total: <span className="font-mono">{fmtINR(qPreview.usedTotal)}</span></div>
              </div>

              {/* EXPENSE ITEMS (Debit) */}
              <div className="mb-4 border-l-4 border-slate-500 bg-slate-50 p-3 rounded-r">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-800 flex items-center gap-1.5">
                    <ArrowRightCircle className="w-4 h-4"/>💼 Expense — Labor / Transport / Other (Debit)
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addExp} className="border-slate-300" data-testid="quick-add-expense"><PlusCircle className="w-3.5 h-3.5 mr-1"/>Add Row</Button>
                </div>
                <div className="text-[9px] uppercase text-slate-500 font-semibold grid grid-cols-11 gap-1 mb-1 px-1">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-1 text-right">Qty</div>
                  <div className="col-span-1">Unit</div>
                  <div className="col-span-2 text-right">Rate ₹</div>
                  <div className="col-span-2 text-right">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="space-y-1">
                {quick.expense_items.map((it, i) => {
                  const amt = (parseFloat(it.qty)||0)*(parseFloat(it.rate)||0);
                  return (
                  <div key={i} className="grid grid-cols-11 gap-1 items-center">
                    <div className="col-span-4"><ItemAutocomplete value={it.item_name} onChange={n=>setExp(i,"item_name",n)} onPick={p=>{const arr=[...quick.expense_items];arr[i]={...arr[i],item_name:p.name,unit:p.unit||arr[i].unit,rate:p.last_rate||arr[i].rate};setQuickField("expense_items",arr);}} placeholder="e.g. Installation Labor" testid={`quick-expense-name-${i}`}/></div>
                    <div className="col-span-1"><Input type="number" step="0.01" className="h-9 text-right font-mono" value={it.qty} onChange={e=>setExp(i,"qty",e.target.value)} data-testid={`quick-expense-qty-${i}`}/></div>
                    <div className="col-span-1"><Input className="h-9" value={it.unit||""} onChange={e=>setExp(i,"unit",e.target.value)} placeholder="Job"/></div>
                    <div className="col-span-2"><Input type="number" step="0.01" className="h-9 text-right font-mono" value={it.rate} onChange={e=>setExp(i,"rate",e.target.value)} data-testid={`quick-expense-rate-${i}`}/></div>
                    <div className="col-span-2 text-right font-mono text-xs font-semibold text-slate-700 pt-2">{fmtINR(amt)}</div>
                    <div className="col-span-1 text-right">{quick.expense_items.length>1 && <Button type="button" size="icon" variant="ghost" onClick={()=>rmExp(i)} className="text-slate-600 h-7 w-7"><Trash className="w-3.5 h-3.5"/></Button>}</div>
                  </div>);
                })}
                </div>
                <div className="text-right text-xs font-bold text-slate-700 mt-2">Expense Total: <span className="font-mono">{fmtINR(qPreview.expTotal)}</span></div>
              </div>

              {/* OTHER CREDIT / DEBIT (side by side) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div className="border-l-4 border-blue-500 bg-blue-50 p-3 rounded-r">
                  <div className="text-xs font-bold uppercase tracking-widest text-blue-800 mb-2 flex items-center gap-1.5">
                    <PlusCircle className="w-4 h-4"/>➕ Other Credit (Refund / Bonus)
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input className="col-span-2" value={quick.other_credit_desc} onChange={e=>setQuickField("other_credit_desc", e.target.value)} placeholder="e.g. Subsidy Received" data-testid="quick-other-credit-desc"/>
                    <Input type="number" step="0.01" value={quick.other_credit} onChange={e=>setQuickField("other_credit", e.target.value)} placeholder="₹" className="font-mono text-right" data-testid="quick-other-credit"/>
                  </div>
                </div>
                <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded-r">
                  <div className="text-xs font-bold uppercase tracking-widest text-amber-800 mb-2 flex items-center gap-1.5">
                    <MinusCircle className="w-4 h-4"/>➖ Other Debit (Misc outflow)
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input className="col-span-2" value={quick.other_debit_desc} onChange={e=>setQuickField("other_debit_desc", e.target.value)} placeholder="e.g. Chai/Snacks/Misc" data-testid="quick-other-debit-desc"/>
                    <Input type="number" step="0.01" value={quick.other_debit} onChange={e=>setQuickField("other_debit", e.target.value)} placeholder="₹" className="font-mono text-right" data-testid="quick-other-debit"/>
                  </div>
                </div>
              </div>

              {/* LIVE PREVIEW — same 8 boxes */}
              <div className="border-2 border-slate-800 rounded p-3 bg-slate-50 mb-3">
                <div className="text-[10px] uppercase font-bold tracking-widest text-slate-600 mb-2">📊 Live Preview — Save karne se pehle calculation</div>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  <div className="border rounded p-2 bg-white text-center">
                    <div className="text-[9px] uppercase font-bold text-emerald-700">Project Amount</div>
                    <div className="text-sm font-black font-mono text-emerald-800" data-testid="q-preview-project">{fmtINR(qPreview.proj)}</div>
                  </div>
                  <div className="border rounded p-2 bg-white text-center">
                    <div className="text-[9px] uppercase font-bold text-rose-700">Total Cost (Used+Exp)</div>
                    <div className="text-sm font-black font-mono text-rose-800" data-testid="q-preview-cost">{fmtINR(qPreview.totalCost)}</div>
                  </div>
                  <div className={`border-2 rounded p-2 bg-white text-center ${qPreview.profit>=0?"border-emerald-500":"border-rose-500"}`}>
                    <div className={`text-[9px] uppercase font-bold ${qPreview.profit>=0?"text-emerald-700":"text-rose-700"}`}>PROFIT</div>
                    <div className={`text-sm font-black font-mono ${qPreview.profit>=0?"text-emerald-800":"text-rose-800"}`} data-testid="q-preview-profit">{fmtINR(qPreview.profit)}</div>
                  </div>
                  <div className="border rounded p-2 bg-white text-center">
                    <div className="text-[9px] uppercase font-bold text-slate-700">Total Entries</div>
                    <div className="text-sm font-black font-mono text-slate-800">{qPreview.entries}</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="border rounded p-1.5 bg-blue-50 text-center">
                    <div className="text-[9px] uppercase font-bold text-blue-800">Other Credit</div>
                    <div className="text-xs font-bold font-mono text-blue-700">{fmtINR(qPreview.oc)}</div>
                  </div>
                  <div className="border rounded p-1.5 bg-amber-50 text-center">
                    <div className="text-[9px] uppercase font-bold text-amber-800">Other Debit</div>
                    <div className="text-xs font-bold font-mono text-amber-700">{fmtINR(qPreview.od)}</div>
                  </div>
                  <div className="border rounded p-1.5 bg-emerald-50 text-center">
                    <div className="text-[9px] uppercase font-bold text-emerald-800">Received</div>
                    <div className="text-xs font-bold font-mono text-emerald-700">{fmtINR(qPreview.rec)}</div>
                  </div>
                  <div className="border rounded p-1.5 bg-slate-100 text-center">
                    <div className="text-[9px] uppercase font-bold text-slate-700">Balance Pending</div>
                    <div className={`text-xs font-bold font-mono ${qPreview.balance>0?"text-rose-700":"text-emerald-700"}`}>{fmtINR(qPreview.balance)}</div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-600">Notes (sabhi entries par apply hoga)</Label>
                <Textarea rows={2} value={quick.notes} onChange={e=>setQuickField("notes", e.target.value)} data-testid="quick-notes"/>
              </div>

              <DialogFooter className="mt-3">
                <Button variant="outline" onClick={()=>setQuickOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveQuick} disabled={quickSaving || !quick.customer_id || qPreview.entries===0} data-testid="save-quick-btn">
                  {quickSaving ? "Saving..." : `Save Full Project (${qPreview.entries} entr${qPreview.entries===1?"y":"ies"}${qPreview.rec>0?" + 1 payment":""})`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary bar */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-700"><ArrowDownCircle className="w-4 h-4"/><div className="text-[10px] uppercase font-bold">Project Amount</div></div>
            <div className="text-lg font-black font-mono text-emerald-800" data-testid="summary-project-amount">{fmtINR(summary.project_amount)}</div>
            <div className="text-[10px] text-slate-500">Customer paid ({summary.project_amount_bucket?.count||0} entries)</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-700"><PlusCircle className="w-4 h-4"/><div className="text-[10px] uppercase font-bold">Other Credit</div></div>
            <div className="text-lg font-black font-mono text-blue-800" data-testid="summary-other-credit">{fmtINR(summary.other_credit)}</div>
            <div className="text-[10px] text-slate-500">Refund / bonus / adjustment</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-rose-700"><ArrowUpCircle className="w-4 h-4"/><div className="text-[10px] uppercase font-bold">Kya Laga</div></div>
            <div className="text-lg font-black font-mono text-rose-800" data-testid="summary-used">{fmtINR(summary.used)}</div>
            <div className="text-[10px] text-slate-500">Material used cost</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-700"><ArrowRightCircle className="w-4 h-4"/><div className="text-[10px] uppercase font-bold">Expense</div></div>
            <div className="text-lg font-black font-mono text-slate-800" data-testid="summary-expense">{fmtINR(summary.expense)}</div>
            <div className="text-[10px] text-slate-500">Labor / transport / misc</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-700"><MinusCircle className="w-4 h-4"/><div className="text-[10px] uppercase font-bold">Other Debit</div></div>
            <div className="text-lg font-black font-mono text-amber-800" data-testid="summary-other-debit">{fmtINR(summary.other_debit)}</div>
            <div className="text-[10px] text-slate-500">Miscellaneous outflow</div>
          </CardContent></Card>
          <Card className={summary.profit>=0?"border-emerald-300 ring-1 ring-emerald-200":"border-rose-300 ring-1 ring-rose-200"}><CardContent className="p-4">
            <div className="flex items-center gap-2"><Wallet className={`w-4 h-4 ${summary.profit>=0?"text-emerald-700":"text-rose-700"}`}/><div className="text-[10px] uppercase font-bold text-slate-700">Net Profit</div></div>
            <div className={`text-xl font-black font-mono ${summary.profit>=0?"text-emerald-700":"text-rose-700"}`} data-testid="summary-profit">{fmtINR(summary.profit)}</div>
            <div className="text-[10px] text-slate-500">Credit {fmtINR(summary.total_credit)} − Debit {fmtINR(summary.total_debit)}</div>
          </CardContent></Card>
        </div>
      )}

      {/* Customer-wise Hisaab — Direct per-customer download / print */}
      {custSummary.length > 0 && (
        <Card><CardContent className="p-5" data-testid="customer-wise-hisaab">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Customer-wise Hisaab</div>
              <div className="text-[11px] text-slate-500">Har customer ka alag summary — CSV / PDF single click me</div>
            </div>
            <div className="text-[10px] text-slate-500">{custSummary.length} customer{custSummary.length===1?"":"s"}</div>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Project Amount</TableHead>
              <TableHead className="text-right">Other Cr</TableHead>
              <TableHead className="text-right">Used</TableHead>
              <TableHead className="text-right">Expense</TableHead>
              <TableHead className="text-right">Other Db</TableHead>
              <TableHead className="text-right">Net Profit</TableHead>
              <TableHead className="text-right">Entries</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
            {custSummary.map((g, idx) => (
              <TableRow key={g.customer_id||idx} data-testid={`customer-summary-row-${g.customer_id||'none'}`}>
                <TableCell>
                  <div className="font-semibold text-slate-900">{g.customer_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{g.customer_mobile} {g.customer_city?`· ${g.customer_city}`:""}</div>
                </TableCell>
                <TableCell className="text-right font-mono text-emerald-700">{fmtINR(g.project_amount)}</TableCell>
                <TableCell className="text-right font-mono text-blue-700 text-xs">{fmtINR(g.other_credit)}</TableCell>
                <TableCell className="text-right font-mono text-rose-700">{fmtINR(g.used)}</TableCell>
                <TableCell className="text-right font-mono text-slate-700">{fmtINR(g.expense)}</TableCell>
                <TableCell className="text-right font-mono text-amber-700 text-xs">{fmtINR(g.other_debit)}</TableCell>
                <TableCell className={`text-right font-mono font-bold ${g.profit>=0?"text-emerald-700":"text-rose-700"}`} data-testid={`cust-profit-${g.customer_id||'none'}`}>{fmtINR(g.profit)}</TableCell>
                <TableCell className="text-right text-xs text-slate-500">{g.entries}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={()=>{ setQuick({...emptyQuick, customer_id: g.customer_id||""}); setQuickOpen(true); }} className="h-7 px-2 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-[10px]" data-testid={`row-edit-${g.customer_id||'none'}`} title="Add more entries to this customer" disabled={!g.customer_id}>
                      <Pencil className="w-3 h-3 mr-0.5"/>Add
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>download("csv", g.customer_id||"")} className="h-7 px-2 border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-[10px]" data-testid={`row-csv-${g.customer_id||'none'}`} title="Download CSV" disabled={!g.customer_id}>
                      <Download className="w-3 h-3 mr-0.5"/>CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>openPrint(g.customer_id||"")} className="h-7 px-2 border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 text-[10px]" data-testid={`row-pdf-${g.customer_id||'none'}`} title="Print / Save as PDF" disabled={!g.customer_id}>
                      <Printer className="w-3 h-3 mr-0.5"/>PDF
                    </Button>
                    <Button size="sm" variant="ghost" onClick={()=>setCustomerFilter(g.customer_id||"")} className="h-7 px-2 text-[10px]" data-testid={`row-view-${g.customer_id||'none'}`} title="View entries below" disabled={!g.customer_id}>
                      View →
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      {/* Filters */}
      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo} onClear={()=>{setFFrom("");setFTo("");setSearch("");setCatFilter("ALL");setProjectFilter("");setCustomerFilter("");}}
        extra={
          <div className="flex gap-2 flex-wrap">
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-44 h-9" data-testid="filter-category"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Categories</SelectItem>
                {CATS.map(c=><SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={customerFilter||"__ALL__"} onValueChange={v=>setCustomerFilter(v==="__ALL__"?"":v)}>
              <SelectTrigger className="w-56 h-9" data-testid="filter-customer"><SelectValue placeholder="All customers"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All Customers</SelectItem>
                {customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={projectFilter||"__ALL__"} onValueChange={v=>setProjectFilter(v==="__ALL__"?"":v)}>
              <SelectTrigger className="w-56 h-9" data-testid="filter-project"><SelectValue placeholder="All projects"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All Projects</SelectItem>
                {projects.map(p=><SelectItem key={p.id} value={p.id}>{p.project_no} — {p.customer_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">Qty × Rate</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search?"No matches":"No entries yet. First entry add karo."}</TableCell></TableRow>}
            {visible.map(r=>(
              <TableRow key={r.id} data-testid={`hisaab-row-${r.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                <TableCell>{catBadge(r.category)}</TableCell>
                <TableCell className="text-xs">
                  {r.customer_id ? <span className="font-medium text-slate-800">{custName(r.customer_id)}</span> : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{r.item_name}</div>
                  {r.notes && <div className="text-[10px] text-slate-500 truncate max-w-xs">{r.notes}</div>}
                </TableCell>
                <TableCell className="text-right text-xs font-mono">{r.qty} {r.unit} × {fmtINR(r.rate)}</TableCell>
                <TableCell className="text-right font-mono font-bold">{fmtINR(r.amount)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={()=>edit(r)} data-testid={`edit-hisaab-${r.id}`} className="h-8 px-2 border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 text-[11px]">
                      <Pencil className="w-3.5 h-3.5 mr-1"/>Edit
                    </Button>
                    <DeleteButton title="Delete entry?" description="This cannot be undone." onConfirm={()=>del(r)} testid={`delete-hisaab-${r.id}`}/>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
